import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SendSharp from "@mui/icons-material/SendSharp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IconButton } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import CustomTooltip from "./CustomTooltip";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { createRoot } from "react-dom/client";
import "../../assets/css/ag-grid-custom-themes.css";
import "../../index.css";
import GridLoader from "./GridLoader";
import GridOverlay from "./GridOverlay";
import CustomPaginationPanel from "./CustomPaginationPanel";

const autoSizeStrategy = {
  type: "fitGridWidth",
  defaultMinWidth: 60,
};

const CustomAgGrid = ({
  rows,
  columns,
  handleDelete,
  handleEdit,
  handleView,
  refSet,
  handleEmailSend,
  setGridApi,
  disableFilter = false,
  pagination = true,
  domLayout,
  pageName,
  handleDownload,
  onSelectionChanged,
  isRowSelectable,
  colDef,
  disableToolTip = false,
  isLoading = false,
  headerLayer = 1,
  height,
  setFilterModel,
  setSortModel,
  totalCount,
  pageIndex,
  pageSize,
  setPageIndex,
  setPageSize,
  sortable,
  resizable,
  paginationSize = 10,
  onPaginationChanged,
  gridRefs,
  actionwidth = 150,
  onFilteredCountChange,
  sendFilteredCount = false
}) => {

  const gridRef = useRef();
  const { color } = useSelector((state) => state.theme);
  const [gridHeight, setGridHeight] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    const height = calculateGridHeight(rows?.length);
    if (!isLoading) {
      setGridHeight(height);
    }
  }, [rows]);

  useEffect(() => {
    updateOverlay();
  }, [rows, loading]);

  const calculateGridHeight = (rowCount) => {
    const rowHeight = 50;
    const headerHeight = 50;
    const extraHeight = 15;

    if (rowCount === 0) return ` ${200 + headerHeight * headerLayer}px`;
    if (rowCount <= 5)
      return `${5 * rowHeight + headerHeight * headerLayer + extraHeight}px`;
    return `${10 * rowHeight + headerHeight * headerLayer + extraHeight}px`;
  };

  //changes for report
  // const calculateTotals = useCallback(() => {
  //   if (!rows || rows.length === 0) {
  //     setPinnedBottomRowData([]);
  //     return;
  //   }



  //   const totals = {};

  //   columns.forEach((col) => {
  //     if (typeof rows[0]?.workOrderNo === "string") {
  //       return;
  //     } else if (typeof rows[0][col?.field] === "number") {
  //       // Calculate sum and convert to string
  //       totals[col.field] = rows
  //         .reduce((sum, row) => sum + (row[col.field] || 0), 0)
  //         .toString();
  //       totals[columns[0]?.field] = "Grand Total";
  //     } else {
  //       totals[col?.field] = null; // or use any default value
  //     }
  //   });


  //   // totals[columns[0].field] = 'Grand Total'; // First column name to display 'Grand Total'
  //   setPinnedBottomRowData([totals]);
  // }, [rows, columns]);

  // useEffect(() => {
  //   calculateTotals();
  // }, [rows, calculateTotals]);

  const clearOverlay = () => {
    if (gridRef.current) {
      gridRef.current.hideOverlay();
    }
    const gridContainer = document.querySelector(`div[grid-id='${gridRef.current?.getGridId()}']`);
    if (!gridContainer) return;
    const existingOverlays = gridContainer.querySelectorAll(
      `.no-records-found-container-${gridRef.current?.getGridId()},.loader-container-${gridRef.current?.getGridId()}`
    );
    existingOverlays.forEach((el) => el.remove());
  };

  const updateOverlay = () => {
    clearOverlay();
    const gridContainer = document.querySelector(`div[grid-id='${gridRef.current?.getGridId()}']`);
    const gridBody = gridContainer?.querySelector(".ag-body-viewport");
    if (!gridBody) return;

    const overlayContainer = document.createElement("div");
    overlayContainer.className = loading
      ? `loader-container-${gridRef.current?.getGridId()}`
      : `no-records-found-container-${gridRef.current?.getGridId()}`;
    if (loading || rows?.length === 0) {
      gridBody.appendChild(overlayContainer);
      const root = createRoot(overlayContainer);
      root.render(loading ? <GridLoader /> : <GridOverlay />);
    }
  };

  const convertFilterModelToArray = (filterModel) => {
    return Object.keys(filterModel).map((key) => {
      const { type, filter } = filterModel[key];
      return {
        FieldName: key,
        Condition: type.charAt(0).toUpperCase() + type.slice(1), // Capitalize the first letter of the condition
        Value: filter,
      };
    });
  };

  const onFilterChangedCheck = async () => {
    if (sendFilteredCount && onFilteredCountChange) {
      const filteredRowCount = gridRefs?.current?.api?.getDisplayedRowCount();
      onFilteredCountChange(filteredRowCount); // Notify the parent about the change
      await new Promise((resolve) => setTimeout(resolve, 0)); // Allow React to render the loader before heavy work

    }
  };
  const onFilterChanged = useCallback(() => {
    clearOverlay();
    const gridContainer = document.querySelector(`div[grid-id='${gridRef.current?.getGridId()}']`);
    const gridBody = gridContainer.querySelector(".ag-body-viewport");
    if (!gridBody) return;
    if (!pagination) {
      setPageIndex(1)
      setFilterModel(
        convertFilterModelToArray(gridRef.current?.getFilterModel())
      );
    }
    const totalRows = gridRef.current?.getDisplayedRowCount() || 0;
    if (totalRows === 0) {
      const overlayContainer = document.createElement("div");
      overlayContainer.className = `no-records-found-container-${gridRef.current?.getGridId()}`;
      gridBody.appendChild(overlayContainer);
      const root = createRoot(overlayContainer);
      root.render(<GridOverlay />);
    }
  }, []);

  function convertSortingModelToArray(sortModel) {
    return sortModel.map((sortItem) => {
      return {
        PropertyName: sortItem.colId,
        Order: sortItem.sort,
      };
    });
  }
  const onSortChanged = useCallback((gridApi) => {
    if (!pagination) {
      setPageIndex(1)
      setSortModel(
        convertSortingModelToArray(gridApi?.api?.sortController?.getSortModel())
      );
    }
  }, []);

  const paginationRootRef = useRef(null);

  useEffect(() => {
    const gridBody = document.querySelector(".ag-root-wrapper");
    if (!gridBody || !pageIndex || !pageSize) return;

    // Check if the custom pagination container already exists
    let paginationBody = document.querySelector(".custom-pagination-container");
    if (!paginationBody) {
      // Create the container only if it doesn't exist
      paginationBody = document.createElement("div");
      paginationBody.className = "custom-pagination-container";
      gridBody.appendChild(paginationBody);

      // Create the root and store it in the ref
      paginationRootRef.current = createRoot(paginationBody);
    }

    // Render or update the CustomPaginationPanel using the existing root
    paginationRootRef.current?.render(
      (totalCount > 0) && <CustomPaginationPanel
        totalCount={totalCount}
        pageIndex={pageIndex}
        selectedPageSize={pageSize}
        setSelectedPageSize={setPageSize}
        setSelectedPageIndex={setPageIndex}
      />
    );
  }, [totalCount, pageIndex, pageSize]);

  const onGridReady = useCallback(
    (params) => {
      params.api.sizeColumnsToFit();
      setGridApi(params.api);
      gridRef.current = params.api;
      gridRef.current.hideOverlay();
      gridRef.current.addEventListener("filterChanged", onFilterChanged);
      gridRef.current.addEventListener("sortChanged", onSortChanged);
      // Cleanup event listener on component unmount
      return () => {
        gridRef.current.removeEventListener("filterChanged", onFilterChanged);
        gridRef.current.removeEventListener(
          "sortChanged",
          onSortChanged(gridRef.current)
        );
      };
    },
    [setGridApi, totalCount]
  );

  const actionsRenderer = useCallback(
    ({ data }) => (
      <div>
        {handleEdit && (
          <CustomTooltip title="Edit">
            <IconButton
              onClick={() => handleEdit(data)}
              data-testid="edit-button"
            >
              <EditIcon />
            </IconButton>
          </CustomTooltip>
        )}
        {handleView && (
          <CustomTooltip title="View">
            <IconButton
              onClick={() => handleView(data)}
              data-testid="view-button"
            >
              <VisibilityIcon />
            </IconButton>
          </CustomTooltip>
        )}
        {handleDelete && (
          <CustomTooltip title="Delete">
            <IconButton
              onClick={() => handleDelete(data)}
              data-testid="delete-button"
              disabled={data?.size === ''}
            >
              <DeleteIcon />
            </IconButton>
          </CustomTooltip>
        )}
        {handleEmailSend && (
          <CustomTooltip title="Email">
            <IconButton
              onClick={() => handleEmailSend(data)}
              data-testid="email-button"
            >
              <SendSharp />
            </IconButton>
          </CustomTooltip>
        )}
        {handleDownload && data.enableDownload && (
          <CustomTooltip title="Download">
            <IconButton onClick={() => handleDownload(data)}>
              <FileDownloadIcon />
            </IconButton>
          </CustomTooltip>
        )}
      </div>
    ),
    [handleDelete, handleEdit, handleView, handleEmailSend, handleDownload]
  );

  const updatedColumns = useMemo(() => {
    const cols = [...columns];
    if (handleEdit || handleDelete || handleView || handleDownload) {
      cols.push({
        headerName: "Actions",
        field: "actions",
        cellRenderer: actionsRenderer,
        sortable: false,
        filter: false,
        width: 150,
        minWidth: actionwidth,
      });
    }
    return cols;
  }, [
    columns,
    actionsRenderer,
    handleEdit,
    handleDelete,
    handleView,
    handleDownload,
  ]);

  const onCellDoubleClicked = useCallback((event) => {
    if (event.colDef.field && event.value) {
      const textToCopy = event.value;
      const input = document.createElement("input");
      input.setAttribute("value", textToCopy);
      document.body.appendChild(input);
      input.select();
      input.setSelectionRange(0, 99999);
      try {
        document.execCommand("copy");
        //setCopyMessage(`Copied to clipboard: ${textToCopy}`);
        setTimeout(() => setCopyMessage(""), 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
      document.body.removeChild(input);
    }
  }, []);
  return (
    <div>
      {copyMessage && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            padding: "10px",
            backgroundColor: "#4caf50",
            color: "#fff",
            borderRadius: "4px",
          }}
        >
          {copyMessage}
        </div>
      )}
      <div
        className={`${color === "dark"
          ? "ag-theme-quartz-dark ag-theme-custom-dark"
          : `ag-theme-quartz ag-theme-quartz-${color}`
          }`}
        style={{
          height: height ? height : gridHeight,
          width: "100%",
          position: "relative",
        }}
      >
        <AgGridReact
          ref={gridRefs}
          suppressRowClickSelection={true} // Prevent row selection on row click
          isRowSelectable={isRowSelectable} // Allow only "Completed" rows to be selectable
          onPaginationChanged={onPaginationChanged}
          autoSizeStrategy={autoSizeStrategy}
          refSet={refSet}
          domLayout={domLayout}
          rowData={rows}
          onFilterChanged={onFilterChangedCheck} // Triggered when filter changes
          columnDefs={updatedColumns}
          onGridReady={onGridReady}
          pagination={pagination}
          tooltipShowDelay={500}
          paginationPageSize={paginationSize != 10 ? paginationSize : 10}
          rowSelection="multiple"
          paginationPageSizeSelector={paginationSize != 10 ? [paginationSize] : [10, 20, 50]}
          suppressCellSelection
          onRowClicked={(event) => console.log(event.data)}
          onSelectionChanged={onSelectionChanged}
          onCellDoubleClicked={onCellDoubleClicked}
          defaultColDef={
            disableFilter
              ? { flex: 1, sortable: sortable, resizable: resizable, autoHeight: true, minWidth: 150, }
              : {
                ...colDef,
                sortable: sortable,
                flex: 1,
                minWidth: 150,
                filter: "agTextColumnFilter",
                filterParams: {
                  filterOptions: ["contains", "startsWith", "endsWith"],
                  defaultOption: "contains",
                  suppressAndOrCondition: true,
                },
                sortable: true,
                floatingFilter: true,
                resizable: true,
                wrapHeaderText: true,
                autoHeaderHeight: true,
                autoHeight: true,
                tooltipValueGetter: (params) =>
                  !disableToolTip && params.value ? `${params.value}` : null,
              }
          }
          {...(pageName === "Reports" && {
            pinnedBottomRowData: pinnedBottomRowData,
          })} // Conditionally set pinnedBottomRowData
          {...(pageName === "Reports" && {
            pinnedBottomRowData: pinnedBottomRowData,
          })} // Conditionally set pinnedBottomRowData
        />
      </div>
    </div>
  );
};

CustomAgGrid.defaultProps = {
  handleDelete: null,
  handleEdit: null,
  handleView: null,
  handleDownload: null,
};

export default CustomAgGrid;
